import React, { useRef } from 'react';
import { AnyAction } from '@reduxjs/toolkit';
import { MailDelivery } from 'lib/types';
import { CourtHouse as CourthousesList } from 'lib/enums';
import { PlusCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import { TextField } from 'lib/components/TextField';
import { ColumnButton } from 'lib/components/ColumnButton';
import { ColumnSelect } from 'lib/components/ColumnSelect';
import {
  actions as affidavitRecipientActions,
  MAX_MAIL_COPIES
} from './deliveryRecipients.slice';

const {
  deleteCourthouse,
  updateCourthouseRecipientCopies,
  selectCourthouseRecipient,
  addCourthouseRecipient
} = affidavitRecipientActions;

type CourthouseRecipientManagerProps = {
  recipients: MailDelivery[];
  dispatch: React.Dispatch<AnyAction>;
  publisherOrganizationStateId: number;
  errors: string[];
  showErrors: boolean;
};

export function CourthouseRecipientManager({
  recipients,
  dispatch,
  publisherOrganizationStateId,
  errors,
  showErrors
}: CourthouseRecipientManagerProps) {
  const nextId = useRef(0);
  const keyMapRef = useRef<number[]>([]);
  while (keyMapRef.current.length < recipients.length) {
    keyMapRef.current.push(nextId.current++);
  }
  if (keyMapRef.current.length > recipients.length) {
    keyMapRef.current.length = recipients.length;
  }

  const courthouseOptions = CourthousesList.map(courthouse => ({
    value: String(courthouse.id),
    label: courthouse.name
  })).sort((a, b) => a.label.localeCompare(b.label));

  return (
    <div className="my-2">
      {recipients.map((a, index) => {
        return (
          <div
            key={keyMapRef.current[index]}
            className="bg-gray-100 border rounded-md p-6 my-5"
          >
            <fieldset className="grid grid-cols-12 gap-5">
              <div className="col-span-12 flex justify-between">
                <legend className="text-sm text-grey-400 font-medium uppercase">
                  Courthouse details
                </legend>
                {index > 0 && (
                  <ColumnButton
                    destructive
                    link
                    aria-label="Delete courthouse"
                    startIcon={<TrashIcon className="w-5 h-5" />}
                    onClick={() => {
                      keyMapRef.current.splice(index, 1);
                      dispatch(deleteCourthouse(index));
                    }}
                    type="button"
                  />
                )}
              </div>
              <div className="col-span-6">
                <ColumnSelect
                  id={`courtHouses${index}`}
                  labelText="Which courthouse?"
                  options={courthouseOptions}
                  onChange={courthouseId => {
                    const selectedCourthouse = CourthousesList.find(
                      courthouse => courthouse.id === Number(courthouseId)
                    );
                    if (selectedCourthouse) {
                      dispatch(
                        selectCourthouseRecipient({
                          index,
                          selectedCourthouse,
                          addressState: publisherOrganizationStateId
                        })
                      );
                    }
                  }}
                  value={String(a.courtHouse)}
                  allowUndefined
                />
              </div>
              <div className="col-span-6">
                <TextField
                  id={`courthouse-copies${index}`}
                  type="number"
                  labelText="How many copies should be sent?"
                  placeholder="Copies"
                  value={String(a.copies)}
                  min={1}
                  max={MAX_MAIL_COPIES}
                  onChange={newValue => {
                    dispatch(
                      updateCourthouseRecipientCopies({
                        newValue: Number(newValue),
                        index
                      })
                    );
                  }}
                />
              </div>
              {showErrors && errors[index] && (
                <div className="col-span-12 text-red-600 text-sm font-normal">
                  {errors[index]}
                </div>
              )}
            </fieldset>
          </div>
        );
      })}
      <ColumnButton
        secondary
        link
        id="addMoreCourthouses"
        startIcon={<PlusCircleIcon className="w-5 h-5" />}
        onClick={() => dispatch(addCourthouseRecipient())}
        buttonText="Add more courthouses"
        type="button"
      />
    </div>
  );
}
